import { createContext, useContext, useEffect, useState } from "react";

const DICT = {
  en: {
    nav: ["Home", "Order pack", "Installation", "Contact us"],
    heroEyebrow: "Furniture packs for compact living",
    heroLines: ["Every square", "metre, ", "wisely", "furnished."],
    heroBody: "Curated, ready-to-live furniture packs designed around the size of your home — from a 20 m² studio to a 40 m² T1. Pick your square metres, we handle the rest.",
    heroCta: "Choose your pack",
    heroContact: "Book an appointment with a handyman/furniture installer",
    stats: ["Curated packs", "Square metres", "Single order"],
    badge: "CURATED PACKS · 20 — 40 M² · SPACE WISE ·",
    heroCard: "Studio · Studio pack",
    marquee: ["Studio", "T1", "Curated", "Delivered as one", "Made for small spaces", "Ready to live"],
    methodEyebrow: "The method",
    methodTitle: ["Small homes deserve ", "considered", " furniture, not leftovers."],
    steps: [
      ["Tell us your m²", "Choose the pack that matches your floor area — studio or small apartment."],
      ["We curate the set", "Every piece is selected to fit together: proportions, storage and finishes that suit compact rooms."],
      ["Unpack and live", "Your whole home arrives as one coordinated order. No endless shopping, no mismatched pieces."],
    ],
    orderEyebrow: "02 — Order pack",
    orderTitle: ["Two packs. ", "One", " for your m²."],
    orderBody: "Each pack is a complete, coordinated home — shown here exactly as it lives in a real Parisian apartment.",
    pictured: "Pictured pieces",
    orderBtn: "Order my pack",
    paypalNote: "* Pay in 3 or 4 instalments",
    instalmentPrefix: "or ",
    installEyebrow: "03 — Installation",
    installTitle: ["Need a hand to ", "assemble", " it all?"],
    installBody: "Book a technician installer who comes to your home and assembles your Space Wise furniture for you — so you can skip the instructions and start living in your new space.",
    installSteps: ["Pick a date that suits you", "A technician comes to your home", "Your furniture is assembled and ready"],
    installCta: "Book an installer",
    installNote: "Booking is made on yoojo.fr",
    installTag: "Assembly at home",
    installAlt: "Technician assembling furniture with a drill",
    contactEyebrow: "04 — Contact us",
    contactTitle: ["Let's talk about ", "your", " space."],
    contactBody: "Not sure which pack fits your floor plan? Send us your square metres and a few words about how you live — we'll reply straight to your inbox.",
    fields: ["Name", "Email", "Subject", "Message"],
    send: "Send message",
    sent: "Message sent — we'll reply to your email soon.",
    tooMany: "Too many messages — please try again later.",
    failed: "Your message couldn't be sent. Please try again.",
    unavailable: "Messaging is unavailable right now.",
    rights: "© 2026 Space Wise. All rights reserved.",
    tagline: "Curated furniture packs for small spaces.",
  },
  fr: {
    nav: ["Accueil", "Commander un pack", "Installation", "Contact"],
    heroEyebrow: "Packs de meubles pour petits espaces",
    heroLines: ["Chaque mètre", "carré, ", "sagement", "meublé."],
    heroBody: "Des packs de meubles sélectionnés et prêts à vivre, pensés selon la taille de votre logement — du studio de 20 m² au T1 de 40 m². Choisissez vos mètres carrés, on s'occupe du reste.",
    heroCta: "Choisir mon pack",
    heroContact: "Prendre rendez-vous avec un bricoleur/installateur de meubles",
    stats: ["Packs sélectionnés", "Mètres carrés", "Une seule commande"],
    badge: "PACKS SÉLECTIONNÉS · 20 — 40 M² · SPACE WISE ·",
    heroCard: "Studio · Pack studio",
    marquee: ["Studio", "T1", "Sélectionné", "Livré en une fois", "Pensé pour les petits espaces", "Prêt à vivre"],
    methodEyebrow: "La méthode",
    methodTitle: ["Les petits logements méritent des meubles ", "réfléchis", ", pas des restes."],
    steps: [
      ["Indiquez vos m²", "Choisissez le pack adapté à votre surface — studio ou T1."],
      ["Nous composons l'ensemble", "Chaque pièce est choisie pour s'accorder : proportions, rangements et finitions adaptés aux petites surfaces."],
      ["Déballez et vivez", "Tout votre intérieur arrive en une seule commande coordonnée. Fini les achats interminables et les meubles dépareillés."],
    ],
    orderEyebrow: "02 — Commander un pack",
    orderTitle: ["Deux packs. ", "Un", " pour vos m²."],
    orderBody: "Chaque pack est un intérieur complet et coordonné — présenté tel qu'il vit dans un véritable appartement parisien.",
    pictured: "Pièces présentées",
    orderBtn: "Commander mon pack",
    paypalNote: "* Payable en 3 ou 4 fois",
    instalmentPrefix: "soit ",
    installEyebrow: "03 — Installation",
    installTitle: ["Besoin d'aide pour tout ", "monter", "\u00a0?"],
    installBody: "Réservez un technicien installateur qui vient chez vous et monte vos meubles Space Wise — oubliez les notices et profitez tout de suite de votre nouvel intérieur.",
    installSteps: ["Choisissez la date qui vous convient", "Un technicien se déplace chez vous", "Vos meubles sont montés et prêts"],
    installCta: "Réserver un installateur",
    installNote: "La réservation se fait sur yoojo.fr",
    installTag: "Montage à domicile",
    installAlt: "Technicien montant un meuble avec une perceuse",
    contactEyebrow: "04 — Contact",
    contactTitle: ["Parlons de ", "votre", " espace."],
    contactBody: "Vous hésitez sur le pack adapté à votre plan ? Envoyez-nous votre surface et quelques mots sur votre façon de vivre — nous vous répondrons directement par e-mail.",
    fields: ["Nom", "E-mail", "Objet", "Message"],
    send: "Envoyer le message",
    sent: "Message envoyé — nous vous répondrons très vite par e-mail.",
    tooMany: "Trop de messages — veuillez réessayer plus tard.",
    failed: "Votre message n'a pas pu être envoyé. Veuillez réessayer.",
    unavailable: "La messagerie est indisponible pour le moment.",
    rights: "© 2026 Space Wise. Tous droits réservés.",
    tagline: "Packs de meubles sélectionnés pour petits espaces.",
  },
};

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: DICT.en });

const initial = () => {
  const saved = localStorage.getItem("sw-lang");
  if (saved === "en" || saved === "fr") return saved;
  return navigator.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(initial);
  useEffect(() => {
    localStorage.setItem("sw-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  return <LangCtx.Provider value={{ lang, setLang, t: DICT[lang] }}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);
